import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import './AuthPages.css';

export default function SignupPage({ onLogin }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');

    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (authError) throw authError;

      // Email confirmation required — session will be null
      if (!data.session) {
        // Supabase returns an empty identities array when the email is already registered
        if (data.user?.identities?.length === 0) {
          setError('An account with this email already exists. Please log in instead.');
          return;
        }
        setConfirmEmail(email);
        return;
      }

      const currentUser = { id: data.user.id, email: data.user.email, name, orgId: null, role: 'member', avatarUrl: null };
      await onLogin(currentUser, data.session.access_token, true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  if (confirmEmail) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-logo">
            <img src="/logo.png" alt="DealNexa" className="auth-logo-img" />
          </div>
          <h2 className="auth-title">Check your email</h2>
          <p className="auth-subtitle">We sent a confirmation link to <strong>{confirmEmail}</strong>. Click it to activate your account.</p>
          <button className="auth-back" onClick={() => navigate('/login')}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-logo">
          <img src="/logo.png" alt="DealNexa" className="auth-logo-img" />
        </div>
        <h2 className="auth-title">Create Account</h2>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="name">Full Name</label>
            <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your full name" />
          </div>
          <div className="auth-field">
            <label htmlFor="email">Work Email</label>
            <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
          </div>
          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <div className="auth-password-wrapper">
              <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" />
              <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>
        <div className="auth-divider"><span>or</span></div>
        <button className="auth-google-btn" onClick={handleGoogleSignIn} disabled={googleLoading}>
          {googleLoading ? 'Connecting...' : (
            <>
              <GoogleIcon />
              Continue with Google
            </>
          )}
        </button>
        <p className="auth-switch">Already have an account? <Link to="/login">Log in</Link></p>
        <button className="auth-back" onClick={() => navigate('/')}>Back to home</button>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.842 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.58-5.036-3.711H.96v2.332C2.44 15.983 5.48 18 9.003 18z"/>
      <path fill="#FBBC05" d="M3.964 10.712c-.18-.54-.282-1.116-.282-1.71s.102-1.17.282-1.71V4.96H.96C.348 6.173 0 7.548 0 9.002s.348 2.829.96 4.042l3.004-2.332z"/>
      <path fill="#EA4335" d="M9.003 3.58c1.32 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.48 0 2.44 2.017.96 4.958L3.964 7.29c.708-2.13 2.692-3.71 5.039-3.71z"/>
    </svg>
  );
}
